<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center text-black transition-opacity duration-300 bg-[#0000009e] backdrop-blur-[7px]"
    @click.self="closeModal"
  >
    <div
      class="relative flex flex-col items-center justify-end w-full h-full max-w-full p-6 rounded-lg"
      :class="[$root.hotelCode === 'taigara' ? 'bg-[#455E4C] text-white' : '']"
    >
      <!-- Modal Content -->
      <div class="relative z-10 flex flex-col items-start h-full">
        <div class="flex flex-col items-center justify-end h-full">
          <!-- microphone permission -->
          <div
            v-if="microphonePermission !== 'granted'"
            class="w-full max-w-full mx-auto mt-4 bg-blue-500 text-white rounded-lg shadow-md p-4 flex items-start gap-3"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-8 h-8 text-white">
              <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clip-rule="evenodd" />
            </svg>

            <div class="flex-1 flex flex-col items-end">
              <p v-if="microphonePermission === 'prompt'" class="text-xl font-medium">
                To start Voice AI, please enable microphone access.
              </p>
              <p v-if="microphonePermission === 'denied'" class="text-xl font-medium">
                Microphone access was denied. Please enable it manually in your browser settings and reload the page.
              </p>
              <p v-if="microphonePermission === 'denied' && micError === 'notReadable'" class="text-xl font-medium">
                Microphone is busy. Please close other apps and try again.
              </p>

              <button
                v-if="microphonePermission === 'prompt'"
                @click="requestMicrophoneAccess"
                class="mt-2 text-base px-4 py-2 border-2 border-white/80 rounded-full hover:bg-white hover:text-[#4169E1] transition"
              >
                Enable Microphone
              </button>
            </div>
          </div>
         <!-- /microphone permission -->

          <loading class="mt-auto mb-auto" v-if="loading"></loading>
          <VoiceAnimation v-if="!loading" :isSpeaking="isSpeaking" />

          <!-- v-if="!hasStartedSpeaking && !loading" -->
          <div v-if="!hasStartedSpeaking && !loading && microphonePermission === 'granted'" class="wrapper absolute">
            <div class="typing-demo text-white text-[60px]">
              Say "Hello!"
            </div>
          </div>

          <section class="w-full">
            <SessionControls
              :start-session="startSession"
              :stop-session="stopSession"
              :send-client-event="sendClientEvent"
              :server-events="events"
              @close-modal="closeModal"
            />
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "@/components/article/ArticleLoading.vue"
import SessionControls from "./SessionControls.vue"
import VoiceAnimation from "./VoiceAnimation.vue"

export default {
  name: "VoiceAiWrapper",
  components: {
    SessionControls,
    VoiceAnimation,
    Loading,
  },
  data() {
    return {
      isOpen: false,
      events: [],
      dataChannel: null,
      peerConnection: null,
      audioElement: null,
      isSpeaking: false,
      loading: false,
      microphonePermission: 'prompt',
      hasStartedSpeaking: false
    }
  },
  watch: {
    dataChannel(newDataChannel) {
      if (newDataChannel) {
        newDataChannel.addEventListener("message", (e) => {
          const event = JSON.parse(e.data)
          if (!event.timestamp) {
            event.timestamp = new Date().toLocaleTimeString()
          }
          this.events = [event, ...this.events]
        })

        newDataChannel.addEventListener("open", async () => {
          this.setPrompt()
        })
      }
    },

    isOpen(val) {
      this.$root.freezeActivePageScroll(val)
    },
  },
  methods: {
    async openModal() {
      this.events = []
      this.isSpeaking = false
      this.hasStartedSpeaking = false
      this.microphonePermission = 'prompt'
      this.isOpen = true
      this.loading = true

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach(track => track.stop())
        this.microphonePermission = 'granted'
        await this.startSession()
      } catch (err) {
        if (err.name === 'NotReadableError') {
          // Mic is busy — wait and retry once
          await this.retryMicrophoneAccess()
        } else {
          console.error('Mic error:', err.name, err.message)
          this.microphonePermission = 'denied'
        }
      } finally {
        this.loading = false
      }
    },

    async retryMicrophoneAccess() {
      try {
        // Wait 1.5s for the OS to release the mic
        await new Promise(resolve => setTimeout(resolve, 1500))

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach(track => track.stop())
        this.microphonePermission = 'granted'
        await this.startSession()
      } catch (retryErr) {
        // Retry also failed — show user friendly message
        console.error('Mic retry failed:', retryErr.name, retryErr.message)
        this.microphonePermission = 'denied'
        this.micError = 'notReadable' // optional: show specific UI message
      }
    },

    async requestMicrophoneAccess() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.microphonePermission = 'granted'
        stream.getTracks().forEach(track => track.stop())
        this.loading = true
        await this.startSession()
      } catch (err) {
        alert(`requestMicrophoneAccess error: ${err.name} - ${err.message}`)
        this.microphonePermission = 'denied'
      } finally {
        this.loading = false
      }
    },

    closeModal() {
      this.stopSession()
      this.isOpen = false
    },

    async startSession() {
      try {
        const tokenResponse = await fetch(
          `${process.env.VUE_APP_OPENAI_URL}token`
        )
        const data = await tokenResponse.json()
        const EPHEMERAL_KEY = data.value

        const pc = new RTCPeerConnection()
        this.audioElement = document.createElement("audio")
        this.audioElement.autoplay = true
        pc.ontrack = (e) => {
          const stream = e.streams[0]
          this.audioElement.srcObject = stream

          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)()
          const source = audioContext.createMediaStreamSource(stream)
          const analyser = audioContext.createAnalyser()
          const dataArray = new Uint8Array(analyser.frequencyBinCount)

          source.connect(analyser)

          const checkSpeaking = () => {
            analyser.getByteFrequencyData(dataArray)
            const volume =
              dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length

            if (volume > 10) {
              if (!this.isSpeaking) {
                this.isSpeaking = true
                setTimeout(() => {
                  this.hasStartedSpeaking = true
                }, 600);
              }
            } else {
              if (this.isSpeaking) this.isSpeaking = false
            }

            requestAnimationFrame(checkSpeaking)
          }

          checkSpeaking()
        }

        const ms = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
        pc.addTrack(ms.getTracks()[0])

        const dc = pc.createDataChannel("oai-events")
        this.dataChannel = dc

        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)

        const baseUrl = "https://api.openai.com/v1/realtime/calls"
        const model = "gpt-realtime"

        const sdpResponse = await fetch(
          `${baseUrl}?model=${model}`,
          {
            method: "POST",
            body: offer.sdp,
            headers: {
              Authorization: `Bearer ${EPHEMERAL_KEY}`,
              "Content-Type": "application/sdp",
            },
          }
        )

        const answer = {
          type: "answer",
          sdp: await sdpResponse.text(),
        }
        await pc.setRemoteDescription(answer)

        this.peerConnection = pc
      } catch (error) {
        console.error("Error starting session:", error)
      }
    },

    async setPrompt() {
      try {
        const response = await fetch(
          process.env.VUE_APP_API_URL +
            "public/chat/gpt-prompt/" +
            this.$root.hotel.uuid
        )

        const result = await response.json()

        if (result.success && Array.isArray(result.data)) {
          result.data.forEach((item) => {
            let systemPrompt = {
              type: "conversation.item.create",
              item: {
                type: "message",
                role: item.role || "system",
                content: [
                  {
                    type: "input_text",
                    text: item.content,
                  },
                ],
              },
            }
            this.sendClientEvent(systemPrompt)
          })
        }
        else {
          console.warn("Prompt API failed or returned no data:", result)
        }
      } catch (err) {
        console.error("Failed to fetch system prompts:", err)
      }
    },

    stopSession() {
      if (this.dataChannel) {
        this.dataChannel.close()
      }

      if (this.peerConnection) {
        this.peerConnection.getSenders().forEach((sender) => {
          if (sender.track) {
            sender.track.stop()
          }
        })
        this.peerConnection.close()
      }

      this.dataChannel = null
      this.peerConnection = null
      this.audioElement = null
    },

    sendClientEvent(message) {
      if (!this.dataChannel)
        console.error(
          "Failed to send message - no data channel available",
          message
        )

      const timestamp = new Date().toLocaleTimeString()
      message.event_id = message.event_id || crypto.randomUUID()
      this.dataChannel.send(JSON.stringify(message))
      if (!message.timestamp) {
        message.timestamp = timestamp
      }
      this.events = [message, ...this.events]
    },
  },
}
</script>

<style scoped>
.wrapper {
  top: calc(50% + 100px);
  /*This part is important for centering*/
  display: grid;
  place-items: center;
}

.typing-demo {
  width: 12ch;
  animation: typing 2s steps(22), blink .5s step-end infinite alternate;
  white-space: nowrap;
  overflow: hidden;
  border-right: 3px solid;
  font-family: monospace;
}

@keyframes typing {
  from {
    width: 0
  }
}

@keyframes blink {
  50% {
    border-color: transparent
  }
}
</style>
